import { cloneElement, type JSX } from 'react'
import { Link, useFetcher } from 'react-router'
import { AnimatePresence, LayoutGroup, motion } from 'motion/react'
import { Tooltip, Typography } from '../Display'
import { Box, Grid, Stack } from '../Layout'
import { useMediaQuery, useTheme, useZoomDialog, type SxProps } from '../../hooks'
import { ImageNotFound } from '../../assets'
import { ActionGutter, type Action } from './ActionGutter'
import { AvailabilityChip } from '../Chips/AvailabilityChip'

export function ImageCards<ItemType>({ items, children = undefined }: ImageCardsProps<ItemType>) {
  if (items.length < 1)
    return (
      <Typography variant='h6' align='center'>
        There are no items to display.
      </Typography>
    )

  return (
    <Stack>
      <Grid container spacing={2} justifyContent={{ mobile: 'center', laptop: 'flex-start' }}>
        <LayoutGroup>
          <AnimatePresence mode='popLayout'>
            {items.map(item => (
              <Grid key={item.key}>
                <motion.div
                  layout
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ type: 'tween' }}
                >
                  <ImageCard item={item}>{children ? cloneElement(children(item.item)) : null}</ImageCard>
                </motion.div>
              </Grid>
            ))}
          </AnimatePresence>
        </LayoutGroup>
      </Grid>
    </Stack>
  )
}

export function ImageCard<ItemType>({
  item,
  width = { mobile: 320, laptop: 400 },
  sx = {},
  children = undefined,
}: ImageCardProps<ItemType>) {
  const fetcher = useFetcher({ key: IMAGE_CARD_FETCHER_KEY })
  const theme = useTheme()
  const onMobile = useMediaQuery(theme.breakpoints.between('mobile', 'tablet'))
  const { ZoomDialog, zoomAction } = useZoomDialog(item.image?.zoom_uri)

  const actionsArr = item.image?.zoom_uri ? [...item.actions, zoomAction] : item.actions

  return (
    <>
      <fetcher.Form method='POST'>
        <motion.div layout transition={{ duration: 0.25 }}>
          <Stack
            py={4}
            width={width}
            boxShadow={theme => theme.elevation.card2}
            borderRadius={theme => theme.shape.borderRadius}
            sx={{ p: 0, overflow: 'hidden', ...sx }}
            position='relative'
          >
            <Stack flex={2} bgcolor='neutral.extraLight' alignItems='center'>
              {item.stock && (
                <Box sx={{ position: 'absolute', top: '0.75rem', left: '0.75rem' }}>
                  <AvailabilityChip availability={item.stock.availability} severity={item.stock.severity} />
                </Box>
              )}
              <img
                src={item.image?.uri ?? ImageNotFound}
                alt={item.image?.description ?? ''}
                height={onMobile ? width.mobile * 0.71 : width.laptop * 0.9}
              />
              <ActionGutter actions={actionsArr} />
            </Stack>
            {item.title && (
              <Stack p={2} spacing={{ mobile: 2, laptop: 2 }}>
                <Stack spacing={1} direction='row' justifyContent='space-between'>
                  <Stack spacing={1}>
                    <Tooltip title={item.title?.tooltip ?? ''}>
                      <Typography variant='h6' sx={{ '& a': { color: theme.palette.grey[500] } }}>
                        {item.title?.link ? <Link to={item.title.link}>{item.title.text}</Link> : item.title.text}
                      </Typography>
                    </Tooltip>
                    {item.subtitle && (
                      <Tooltip title={item.subtitle?.tooltip ?? ''}>
                        <Typography variant='smallBody2' sx={{ '& a': { color: theme.palette.grey[500] } }}>
                          {item.subtitle?.link ? (
                            <Link to={item.subtitle.link}>{item.subtitle.text}</Link>
                          ) : (
                            item.subtitle.text
                          )}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                  {item.description && (
                    <Tooltip title={item.description?.tooltip ?? ''}>
                      <Typography
                        variant='smallBody2'
                        sx={{ '& a': { color: theme.palette.grey[300], textDecorationColor: theme.palette.grey[300] } }}
                      >
                        {item.description?.link ? (
                          <Link to={item.description.link}>{item.description.text}</Link>
                        ) : (
                          item.description.text
                        )}
                      </Typography>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            )}
            {children}
          </Stack>
        </motion.div>
      </fetcher.Form>
      <ZoomDialog />
    </>
  )
}

/** HELPERS */
const IMAGE_CARD_FETCHER_KEY = 'ImageCardFETCHERKEY'
ImageCards.FETCHER_KEY = IMAGE_CARD_FETCHER_KEY
ImageCard.FETCHER_KEY = IMAGE_CARD_FETCHER_KEY

//* ----- TYPES ----- *//
interface HeaderInfo {
  text: string | number
  tooltip?: string
  link?: string
}

interface ImageInfo {
  uri?: string
  fallback?: string
  tooltip?: string
  description?: string
  zoom_uri?: string
}

export interface ImageCardItemsType<ItemType> {
  key: string | number
  title?: HeaderInfo
  subtitle?: HeaderInfo
  description?: HeaderInfo
  image?: ImageInfo
  stock?: {
    availability: string
    severity: 'error' | 'info' | 'none' | 'success' | 'warning'
  }
  actions: Action[]
  item: ItemType
}

/** Props */
interface ImageCardProps<ItemType> {
  item: ImageCardItemsType<ItemType>
  children?: React.ReactNode
  width?: { mobile: number; laptop: number }
  sx?: SxProps
}

interface ImageCardsProps<ItemType> {
  items: ImageCardItemsType<ItemType>[]
  children?: (item: ItemType) => JSX.Element
}
